<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { Artifact } from '../game/inventory-api';
import { itemArt, SHOP_ART } from '../game/art';
import { RARITY_HEX, SLOT_LABELS, scaledStats, STAT_META, effectiveLevel } from '../game/item-math';
import type { EquipSlot } from '../game/inventory-api';
import { t } from '../i18n';

/**
 * CHEST REVEAL — the Wyrm-Hoard Chest opening cinematic. A closed chest
 * rattles, bursts open in a spray of rarity-colored light, and the won piece
 * rises out on a glowing card. Reduced-motion jumps straight to the reveal.
 * Purely presentational — the item is already granted server-side by the
 * time this shows; closing just dismisses.
 */
const props = defineProps<{ reward: Artifact }>();
const emit = defineEmits<{ (e: 'close'): void }>();

// phases: 'shut' (rattle) → 'burst' (open + rays + flash) → 'reveal' (card up)
const phase = ref<'shut' | 'burst' | 'reveal'>('shut');
const timers: number[] = [];

const reduce = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const accent = computed(() => RARITY_HEX[props.reward.rarity]);
const rarityLabel = computed(() =>
  props.reward.rarity.charAt(0) + props.reward.rarity.slice(1).toLowerCase());
const slotLabel = computed(() => SLOT_LABELS[props.reward.type.toLowerCase() as EquipSlot]);
const stats = computed(() => scaledStats(props.reward));
const chestArt = SHOP_ART.chest_pull;

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close');
}

onMounted(() => {
  window.addEventListener('keydown', onKey);
  if (reduce) { phase.value = 'reveal'; return; }
  timers.push(window.setTimeout(() => { phase.value = 'burst'; }, 950));
  timers.push(window.setTimeout(() => { phase.value = 'reveal'; }, 1450));
});
onUnmounted(() => {
  window.removeEventListener('keydown', onKey);
  timers.forEach((t) => window.clearTimeout(t));
});
</script>

<template>
  <div
    class="cr-scrim"
    :style="{ '--rar': accent }"
    @click.self="phase === 'reveal' && emit('close')"
  >
    <!-- legendary pulls gild the whole overlay's edges -->
    <div v-if="reward.rarity === 'LEGENDARY'" class="cr-legend-glow" aria-hidden="true"></div>

    <!-- the slow radial breath behind everything, in the item's rarity light -->
    <div class="cr-breath" aria-hidden="true"></div>

    <!-- radial rays + expanding shockring + flash, tinted to the item's rarity -->
    <div
      v-if="phase !== 'shut'"
      class="cr-rays"
      :class="{ 'is-legend': reward.rarity === 'LEGENDARY' }"
      aria-hidden="true"
    ></div>
    <div v-if="phase === 'burst'" class="cr-ring" aria-hidden="true"></div>
    <div v-if="phase === 'burst'" class="cr-flash" aria-hidden="true"></div>

    <!-- the chest itself — straining, then bursting -->
    <div v-if="phase !== 'reveal'" class="cr-chest-wrap">
      <span class="cr-ground" aria-hidden="true"></span>
      <div class="cr-chest" :class="phase === 'burst' ? 'is-burst' : 'is-shut'">
        <span class="cr-seam" aria-hidden="true"></span>
        <img v-if="chestArt" :src="chestArt" alt="" />
      </div>
    </div>

    <!-- THE PRIZE — no card, no frame. The thing itself, huge, hanging in the
         rarity's light over a pool of its own glow; the words cascade in under
         it. Boxing this moment into a bordered popup was the whole failure. -->
    <div v-if="phase === 'reveal'" class="cr-scene" role="dialog" :aria-label="t('chest.rewardAria')">
      <div class="cr-kicker">{{ t('chest.kicker') }}</div>

      <div class="cr-prize">
        <img
          v-if="itemArt(reward.type, reward.rarity, reward.name)"
          :src="itemArt(reward.type, reward.rarity, reward.name)"
          :alt="reward.name"
          class="cr-prize-art"
        />
        <span class="cr-pool" aria-hidden="true"></span>
        <span class="cr-impact" aria-hidden="true"></span>
      </div>

      <div class="cr-rarity">
        {{ rarityLabel }} ᛭ {{ slotLabel }} ᛭ {{ t('items.modal.tier', { tier: effectiveLevel(reward) }) }}
      </div>
      <h2 class="cr-name" :style="{ color: accent }">{{ reward.name }}</h2>
      <span class="cr-divider" aria-hidden="true"></span>

      <div class="cr-stats">
        <template v-for="(v, k) in stats" :key="k">
          <span v-if="v !== 0" class="cr-stat">
            <b>{{ v }}</b>
            <i>{{ k === 'hp' ? 'HP' : STAT_META[k as 'attack'].short }}</i>
          </span>
        </template>
      </div>

      <p class="cr-note">{{ t('chest.note') }}</p>
      <button class="btn-forge btn-forge--rune btn-forge--lg cr-take" @click="emit('close')">{{ t('chest.takeIt') }}</button>
    </div>
  </div>
</template>

<style scoped>
.cr-scrim {
  position: fixed; inset: 0; z-index: 500;
  display: grid; place-items: center;
  background: rgba(3, 5, 10, 0.92);
  backdrop-filter: blur(4px);
  overflow: hidden;
}

/* ── legendary edge gild — a fixed inset glow, never intercepts clicks ── */
.cr-legend-glow {
  position: absolute; inset: 0; z-index: 5; pointer-events: none;
  box-shadow:
    inset 0 0 120px rgba(201, 169, 92, 0.55),
    inset 0 0 260px rgba(201, 169, 92, 0.28);
}

/* ── breath: a huge, slow pulse of rarity light behind the whole ritual ── */
.cr-breath {
  position: absolute; z-index: 0;
  width: 140vmin; height: 140vmin;
  border-radius: 50%;
  background: radial-gradient(circle, color-mix(in srgb, var(--rar) 38%, transparent) 0%, transparent 66%);
  animation: cr-breathe 4.2s ease-in-out infinite;
}
@keyframes cr-breathe {
  0%, 100% { opacity: 0.32; transform: scale(0.92); }
  50% { opacity: 0.72; transform: scale(1.08); }
}

/* ── rays: a conic fan of rarity light spinning slowly behind the action ── */
.cr-rays {
  position: absolute; z-index: 1; width: 120vmin; height: 120vmin;
  background: repeating-conic-gradient(
    from 0deg,
    color-mix(in srgb, var(--rar) 30%, transparent) 0deg 5deg,
    transparent 5deg 16deg
  );
  mix-blend-mode: screen;
  opacity: 0.22;
  mask-image: radial-gradient(circle, #000 0%, #000 14%, transparent 46%);
  -webkit-mask-image: radial-gradient(circle, #000 0%, #000 14%, transparent 46%);
  animation: cr-spin 26s linear infinite, cr-rays-in 500ms ease both;
}
.cr-rays.is-legend { opacity: 0.34; }
@keyframes cr-spin { to { transform: rotate(360deg); } }
/* implicit `to` — each variant settles at its OWN static opacity (a filled
   `to` would pin the legendary at the base value forever) */
@keyframes cr-rays-in { from { opacity: 0; transform: scale(0.4); } }

/* ── shockring: the fast expanding burst at the moment of reveal ── */
.cr-ring {
  position: absolute; z-index: 2;
  width: 40vmin; height: 40vmin;
  border: 2px solid color-mix(in srgb, var(--rar) 80%, #fff);
  border-radius: 50%;
  box-shadow: 0 0 34px color-mix(in srgb, var(--rar) 60%, transparent);
  animation: cr-ring-out 500ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
@keyframes cr-ring-out {
  0% { transform: scale(0.4); opacity: 0.9; }
  100% { transform: scale(1.6); opacity: 0; }
}

.cr-flash {
  position: absolute; z-index: 2; inset: 0;
  background: radial-gradient(circle at 50% 48%, #fff, color-mix(in srgb, var(--rar) 60%, #fff) 30%, transparent 62%);
  animation: cr-flash 500ms ease-out both;
}
@keyframes cr-flash {
  0% { opacity: 0; }
  22% { opacity: 0.92; }
  100% { opacity: 0; }
}

/* ── chest: bigger, straining on its own ground shadow ── */
.cr-chest-wrap {
  position: relative; z-index: 3;
  width: clamp(240px, 27vw, 300px); height: clamp(240px, 27vw, 300px);
  display: grid; place-items: center;
}
/* the chest art carries transparent padding at its foot — the shadow lives
   INSIDE the wrap, right under the painted box, or the chest reads as afloat */
.cr-ground {
  position: absolute; z-index: 0; left: 50%; bottom: 9%; transform: translateX(-50%);
  width: 62%; height: 24px;
  background: radial-gradient(ellipse, rgba(0, 0, 0, 0.68), transparent 70%);
  filter: blur(2px);
}
.cr-chest {
  position: relative; z-index: 1;
  width: 100%; height: 100%;
  transform-origin: 50% 88%;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.7)) drop-shadow(0 0 18px color-mix(in srgb, var(--rar) 45%, transparent));
}
.cr-chest img { position: relative; z-index: 1; width: 100%; height: 100%; object-fit: contain; }
.cr-seam {
  position: absolute; z-index: 2; left: 10%; right: 10%; top: 46%; height: 3px;
  background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--rar) 85%, #fff) 50%, transparent);
  box-shadow: 0 0 16px 3px color-mix(in srgb, var(--rar) 70%, transparent);
  opacity: 0.3;
}
.cr-chest.is-shut { animation: cr-strain 380ms ease-in-out infinite alternate; }
.cr-chest.is-shut .cr-seam { animation: cr-seam-glow 380ms ease-in-out infinite alternate; }
.cr-chest.is-burst { animation: cr-burst 500ms cubic-bezier(0.3, 0, 0.2, 1) both; }
@keyframes cr-strain {
  from { transform: rotate(-1.5deg) scale(1); }
  to { transform: rotate(1.5deg) scale(1.025); }
}
@keyframes cr-seam-glow {
  from { opacity: 0.25; }
  to { opacity: 0.95; }
}
@keyframes cr-burst {
  0% { transform: scale(1.08); opacity: 1; }
  40% { transform: scale(1.35) translateY(-8px); opacity: 1; }
  100% { transform: scale(2) translateY(-30px); opacity: 0; }
}

/* ── THE PRIZE SCENE — an open-air composition, never a box ─────────────── */
.cr-scene {
  position: relative; z-index: 4;
  display: flex; flex-direction: column; align-items: center;
  text-align: center;
  max-width: min(560px, 92vw);
  padding: 10px;
}
.cr-kicker {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.09em; text-transform: uppercase;
  color: #9db0c6;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.9);
  animation: cr-rise 400ms ease 250ms both;
}

/* the thing itself: huge, lit, breathing over its own pool of light */
.cr-prize { position: relative; margin: 2px 0 6px; }
.cr-prize-art {
  display: block;
  width: clamp(200px, 32vmin, 300px);
  aspect-ratio: 1;
  object-fit: contain;
  position: relative; z-index: 1;
  filter:
    drop-shadow(0 30px 42px rgba(0, 0, 0, 0.8))
    drop-shadow(0 0 36px color-mix(in srgb, var(--rar) 55%, transparent));
  animation:
    cr-land 700ms cubic-bezier(0.2, 0.75, 0.3, 1) both,
    cr-float 4.2s ease-in-out 700ms infinite;
}
/* falls INTO the scene with a two-frame settle, then hangs in the light */
@keyframes cr-land {
  0% { opacity: 0; transform: translateY(-52px) scale(1.6); }
  60% { opacity: 1; transform: translateY(10px) scale(0.96); }
  82% { transform: translateY(-4px) scale(1.015); }
  100% { transform: translateY(0) scale(1); }
}
@keyframes cr-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
/* the pool its light throws on the ground */
.cr-pool {
  position: absolute; z-index: 0;
  left: 50%; bottom: -12px; transform: translateX(-50%);
  width: 66%; height: 30px;
  background: radial-gradient(ellipse, color-mix(in srgb, var(--rar) 60%, transparent), transparent 70%);
  filter: blur(7px);
  animation: cr-pool 4.2s ease-in-out 700ms infinite;
}
@keyframes cr-pool {
  0%, 100% { opacity: 0.75; transform: translateX(-50%) scaleX(1); }
  50% { opacity: 0.45; transform: translateX(-50%) scaleX(0.86); }
}
/* one expanding ellipse where it lands — the thump made visible */
.cr-impact {
  position: absolute; z-index: 0;
  left: 50%; bottom: -8px;
  width: 60%; height: 22px;
  transform: translateX(-50%);
  border: 1.5px solid color-mix(in srgb, var(--rar) 80%, #fff);
  border-radius: 50%;
  opacity: 0;
  animation: cr-impact 520ms ease-out 400ms both;
}
@keyframes cr-impact {
  0% { opacity: 0.85; transform: translateX(-50%) scale(0.5); }
  100% { opacity: 0; transform: translateX(-50%) scale(1.7); }
}

/* the words cascade in under it */
.cr-rarity {
  font-family: var(--font-label, sans-serif);
  font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
  color: color-mix(in srgb, var(--rar) 78%, #fff);
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.9);
  animation: cr-rise 400ms ease 380ms both;
}
.cr-name {
  font-family: var(--font-heading, serif);
  font-size: clamp(26px, 6vw, 38px); font-weight: 700; line-height: 1.1;
  margin-top: 3px;
  text-shadow:
    0 0 26px color-mix(in srgb, var(--rar) 55%, transparent),
    0 3px 8px rgba(0, 0, 0, 0.95);
  animation: cr-rise 400ms ease 470ms both;
}
.cr-divider {
  display: block; width: 190px; height: 9px; margin: 10px auto 0;
  background: var(--orn-divider-gold, none) no-repeat center / contain;
  animation: cr-rise 400ms ease 560ms both;
}
@keyframes cr-rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
}

/* stats: struck values over small carved labels, split by hairlines — no boxes */
.cr-stats {
  display: flex; flex-wrap: wrap; justify-content: center;
  margin-top: 14px;
  animation: cr-rise 400ms ease 640ms both;
}
.cr-stat {
  display: inline-flex; flex-direction: column; align-items: center;
  padding: 0 20px;
}
.cr-stat + .cr-stat { border-left: 1px solid rgba(207, 232, 255, 0.18); }
.cr-stat b {
  font-family: var(--font-heading, serif); font-size: 23px; line-height: 1.05;
  color: #f0e6d0;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 14px color-mix(in srgb, var(--rar) 35%, transparent), 0 2px 5px rgba(0, 0, 0, 0.9);
}
.cr-stat i {
  font-family: var(--font-label, sans-serif); font-style: normal;
  font-size: 9px; letter-spacing: 0.07em; text-transform: uppercase; color: #9db0c6;
  margin-top: 2px;
}
.cr-note {
  font-family: var(--font-body, serif); font-style: italic;
  font-size: 12.5px; color: #9db0c6;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.9);
  margin-top: 14px;
  animation: cr-rise 400ms ease 720ms both;
}
.cr-take { margin-top: 14px; animation: cr-rise 400ms ease 800ms both; }

@media (prefers-reduced-motion: reduce) {
  .cr-breath { animation: none; opacity: 0.45; }
  .cr-rays { animation: none; opacity: 0.35; }
  .cr-prize-art, .cr-pool, .cr-impact,
  .cr-kicker, .cr-rarity, .cr-name, .cr-divider, .cr-stats, .cr-note, .cr-take { animation: none; }
  .cr-impact { opacity: 0; }
}
</style>
